import React, { useState } from "react";
import { Box, Button, Grid, Popover, Typography, useTheme } from "@mui/material";
import { CalendarTodayRounded } from "@mui/icons-material";
import moment from "moment";
import FilterDateRange from "ui-components/FilterDateRange";
import { segmentChipSx } from "./segmentStyles";

const API_FORMAT = "YYYY-MM-DD";
// The server rejects a window longer than 366 inclusive days.
const MAX_SPAN_DAYS = 365;
// IST, matching the timezone the server buckets days in. Building the default
// with a bare moment() would shift the window by a day for anyone outside it.
const IST_OFFSET_MINUTES = 330;

// `ago` ends the window that many days before today, so Yesterday is one day
// ending one day back.
const PRESETS = [
  { key: "today", label: "Today", days: 1 },
  { key: "yesterday", label: "Yesterday", days: 1, ago: 1 },
  { key: "7d", label: "7d", days: 7 },
  { key: "30d", label: "30d", days: 30 },
  { key: "90d", label: "90d", days: 90 },
];

export const rangeForDays = (days, ago = 0) => {
  const to = moment().utcOffset(IST_OFFSET_MINUTES).subtract(ago, "days");
  return {
    from: to.clone().subtract(days - 1, "days").format(API_FORMAT),
    to: to.format(API_FORMAT),
  };
};

// Presets first, custom on demand. Two full outlined date inputs are a lot of
// chrome for a control most people use at its default — and a free-form range
// is the only way to reach the server's 366-day rejection, which the clamp
// below makes unreachable.
const RangeChips = ({ range, onChange }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);

  const activePreset = PRESETS.find((p) => {
    const r = rangeForDays(p.days, p.ago);
    return r.from === range.from && r.to === range.to;
  });

  const handleCustom = (next) => {
    const from = moment(next.from, API_FORMAT);
    const to = moment(next.to, API_FORMAT);
    if (!from.isValid() || !to.isValid()) return onChange(next);

    // Clamp by moving whichever end the user did not just touch.
    if (to.diff(from, "days") > MAX_SPAN_DAYS) {
      if (next.from !== range.from) {
        return onChange({ from: next.from, to: from.clone().add(MAX_SPAN_DAYS, "days").format(API_FORMAT) });
      }
      return onChange({ from: to.clone().subtract(MAX_SPAN_DAYS, "days").format(API_FORMAT), to: next.to });
    }
    return onChange(next);
  };

  const chipSx = (active) => segmentChipSx(theme, active);

  return (
    <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
      {PRESETS.map((p) => (
        <Button
          key={p.key}
          disableElevation
          sx={chipSx(activePreset?.key === p.key)}
          onClick={() => onChange(rangeForDays(p.days, p.ago))}
        >
          {p.label}
        </Button>
      ))}
      <Button
        disableElevation
        startIcon={<CalendarTodayRounded sx={{ fontSize: "0.8rem !important" }} />}
        sx={chipSx(!activePreset)}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        {activePreset
          ? "Custom"
          : `${moment(range.from, API_FORMAT).format("DD MMM")} – ${moment(range.to, API_FORMAT).format("DD MMM")}`}
      </Button>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        // 420px is wider than a 360px phone, which pushed the picker off the
        // right edge with no way to reach it.
        slotProps={{
          paper: {
            sx: {
              padding: 2,
              width: { xs: "calc(100vw - 2rem)", sm: 420 },
              maxWidth: "100%",
              borderRadius: "8px",
            },
          },
        }}
      >
        <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, marginBottom: 1 }}>
          Custom range
        </Typography>
        {/* FilterDateRange returns bare <Grid item>s, so it needs a Grid
            container parent — a CSS-grid Box would mis-lay its full-width
            title item against its two half-width pickers. */}
        <Grid container spacing={2}>
          <FilterDateRange from={range.from} to={range.to} onChange={handleCustom} title="" />
        </Grid>
        <Typography sx={{ fontSize: "0.7rem", marginTop: 1 }} color={theme.palette.customColors.g100}>
          Up to 366 days. A longer selection moves the other end to fit.
        </Typography>
      </Popover>
    </Box>
  );
};

export default RangeChips;
